import type { PackageDefinitionsCache } from '@ms-cloudpack/common-types';
import { getDependencies } from '@ms-cloudpack/package-utilities';

/**
 * Reads a package definition and derives a flat array of dependencies to externalize for bundling.
 *
 * To reduce the chances of accidentally bundling something that should be external, this includes
 * the names of original dependencies of all types (prior to transforms), as well as any
 * `includedDependencies` from config that were added by `createPackageSettingsTransform`.
 */
export async function getExternalsFromPackage(
  options: {
    inputPath: string;
  },
  context: {
    packages: PackageDefinitionsCache;
  },
): Promise<string[]> {
  const { inputPath } = options;
  const { packages } = context;
  const transformedDefinition = await packages.get(inputPath);
  const originalDefinition = await packages.get(inputPath, { disableTransforms: true });

  return Object.keys({
    // Use mostly original dependencies in case of removals by createPackageSettingsTransform.
    ...getDependencies(originalDefinition, { includeAll: true }),
    // peerDependenciesMeta names should be redundant with peerDependencies.
    // But in rare cases (debug@4), it may incorrectly include deps that aren't listed elsewhere...
    // Include them just in case, since we want to externalize as many things as possible.
    ...originalDefinition.peerDependenciesMeta,
    // Also use the transformed deps in case any includedDependencies weren't listed in orignal deps.
    ...transformedDefinition.dependencies,
  });
}
